import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { scanSkud } from '../../api/skud';
import { clearSessionKeepDevicePrefs } from '../../utils/clearSession';

const QR_REGION_ID = 'skud-kiosk-qr-region';

const OUTCOME_STYLES = {
    checked_in: { bg: '#16a34a', title: 'Добро пожаловать!' },
    checked_in_negative: { bg: '#ca8a04', title: 'Добро пожаловать (баланс в минусе)' },
    staff_confirmed: { bg: '#16a34a', title: 'Подтверждено сотрудником' },
    pending_confirmation: { bg: '#ca8a04', title: 'Ждём подтверждения сотрудника' },
    insufficient_balance: { bg: '#dc2626', title: 'Не хватает баланса' },
    already_checked_in: { bg: '#2563eb', title: 'Уже отмечен сегодня' },
    no_active_session: { bg: '#7c3aed', title: 'Выберите группу вручную' },
    staff_no_pending: { bg: '#64748b', title: 'Нет ожидающих подтверждений' },
    invalid_token: { bg: '#dc2626', title: 'QR не распознан' },
};

/**
 * Экран киоска СКУД (Фаза 2). Держит одновременно активными два способа ввода —
 * камеру планшета (html5-qrcode) и невидимое поле, всегда в фокусе, куда "печатает"
 * обычный USB-сканер-эмулятор клавиатуры (или можно ввести код вручную и Enter).
 * Что сработает первым — то и обрабатываем.
 */
export default function KioskScanPage() {
    const [result, setResult] = useState(null); // SkudScanResponseDto | null
    const [busy, setBusy] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [manualInput, setManualInput] = useState('');

    // html5-qrcode вызывает video.play() ВНУТРИ себя, не прокидывая этот промис наружу как
    // часть своего start() — поэтому обернуть его в мой собственный .then/.catch невозможно
    // (см. https://goo.gl/LdLk22 — это официально описанное, безвредное поведение браузера:
    // play() отклоняется, если видео убрали из документа, пока оно ещё грузилось). В обычном
    // вебе это просто тихий warning в консоли, но dev-оверлей CRA (react-error-overlay) ловит
    // ЛЮБОЙ необработанный отказ промиса и рушит всё дерево — глушим точечно именно это
    // сообщение, не трогая остальные необработанные ошибки.
    useEffect(() => {
        const onUnhandledRejection = (event) => {
            const msg = String(event.reason?.message || event.reason || '');
            if (msg.includes('play() request was interrupted')) {
                event.preventDefault();
            }
        };
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        return () => window.removeEventListener('unhandledrejection', onUnhandledRejection);
    }, []);

    const hiddenInputRef = useRef(null);
    const html5QrRef = useRef(null);
    const lastTokenRef = useRef(null);
    const resetTimerRef = useRef(null);
    const busyRef = useRef(false);
    // Пока результат показан на экране, камера (она не размонтируется, просто скрыта за
    // { !result && ... }) продолжает распознавать те же кадры и раньше слала повторный
    // скан на каждый кадр, где ещё виден тот же QR — отсюда "бесконечный скан". Флаг busyRef
    // защищает только от гонки ВНУТРИ одного запроса, а не от повторной отправки уже
    // обработанного токена, пока оператор не убрал QR из кадра/результат не сброшен.
    const resultShownRef = useRef(false);

    const venueName = localStorage.getItem('username') || 'Киоск СКУД';

    useEffect(() => {
        resultShownRef.current = result !== null;
    }, [result]);

    const scheduleReset = useCallback((ms = 4000) => {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = setTimeout(() => {
            setResult(null);
            hiddenInputRef.current?.focus();
        }, ms);
    }, []);

    const handleToken = useCallback(async (token, groupId) => {
        if (!token || busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        lastTokenRef.current = token;
        try {
            const res = await scanSkud(token, groupId);
            setResult(res);
            if (res.outcome !== 'no_active_session') {
                scheduleReset();
            }
        } catch (err) {
            setResult({ outcome: 'invalid_token', studentName: null });
            scheduleReset();
        } finally {
            setBusy(false);
            busyRef.current = false;
        }
    }, [scheduleReset]);

    // Камера. React.StrictMode в dev монтирует эффект дважды подряд (mount → cleanup → mount).
    // Раньше просто проверял состояние сканера перед stop() — но этого мало: если cleanup
    // срабатывает, пока браузер ЕЩЁ выполняет video.play() внутри qr.start() (сам start() ещё
    // не settle-нулся), получаем "play() request was interrupted" — сам браузер ругается, что
    // видео-элемент убрали из документа посреди воспроизведения. Правильный фикс — cleanup
    // должен ДОЖДАТЬСЯ, пока start() полностью завершится (успехом или ошибкой), и только
    // потом останавливать — никогда не обрывать start() на полпути.
    useEffect(() => {
        const qr = new Html5Qrcode(QR_REGION_ID);
        html5QrRef.current = qr;

        const stopSafely = () => {
            try {
                if (qr.getState && qr.getState() === Html5QrcodeScannerState.SCANNING) {
                    qr.stop().then(() => qr.clear()).catch(() => {});
                }
            } catch {
                // защитно — состояние сканера могло смениться между проверкой и вызовом
            }
        };

        const startPromise = qr.start(
            { facingMode: 'environment' },
            { fps: 8, qrbox: { width: 260, height: 260 } },
            (decodedText) => {
                if (!busyRef.current && !resultShownRef.current) handleToken(decodedText);
            },
            () => { /* кадр без QR — норма, игнорируем */ }
        ).catch(() => {
            setCameraError('Камера недоступна — используйте USB-сканер или ввод вручную.');
        });

        return () => {
            // Ждём, пока start() реально закончится (чем бы он ни закончился), прежде чем
            // трогать сканер — вместо того чтобы дёргать stop()/clear() прямо сейчас.
            startPromise.then(stopSafely);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Невидимый инпут для USB HID-сканера (эмулирует клавиатуру + Enter в конце)
    useEffect(() => {
        hiddenInputRef.current?.focus();
        const refocus = () => hiddenInputRef.current?.focus();
        window.addEventListener('click', refocus);
        return () => window.removeEventListener('click', refocus);
    }, []);

    function onHiddenInputKeyDown(e) {
        if (e.key === 'Enter') {
            const value = e.target.value.trim();
            e.target.value = '';
            if (value) handleToken(value);
        }
    }

    function onGroupPick(groupId) {
        if (lastTokenRef.current) handleToken(lastTokenRef.current, groupId);
    }

    function onManualSubmit(e) {
        e.preventDefault();
        if (manualInput.trim()) {
            handleToken(manualInput.trim());
            setManualInput('');
        }
    }

    function logout() {
        clearSessionKeepDevicePrefs();
        window.location.href = '/login';
    }

    const style = result ? OUTCOME_STYLES[result.outcome] : null;

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <div>СКУД — {venueName}</div>
                <button onClick={logout} style={styles.logoutBtn}>Выйти из киоска</button>
            </div>

            {!result && (
                <div style={styles.scanArea}>
                    <div id={QR_REGION_ID} style={styles.qrRegion} />
                    {cameraError && <div style={styles.cameraError}>{cameraError}</div>}
                    <div style={styles.hint}>
                        {busy ? 'Проверяю…' : 'Наведите QR на камеру или отсканируйте USB-сканером'}
                    </div>
                    <input
                        ref={hiddenInputRef}
                        style={styles.hiddenInput}
                        onKeyDown={onHiddenInputKeyDown}
                        autoFocus
                    />
                    <form onSubmit={onManualSubmit} style={styles.manualForm}>
                        <input
                            value={manualInput}
                            onChange={(e) => setManualInput(e.target.value)}
                            placeholder="Код вручную (если не сканируется)"
                            style={styles.manualInput}
                        />
                        <button type="submit" style={styles.manualBtn}>Ок</button>
                    </form>
                </div>
            )}

            {result && result.outcome === 'no_active_session' && (
                <div style={{ ...styles.resultCard, background: style.bg }}>
                    <div style={styles.resultTitle}>{style.title}</div>
                    {result.studentName && <div style={styles.resultSub}>{result.studentName}</div>}
                    <div style={styles.groupList}>
                        {(result.groupOptions || []).map((g) => (
                            <button key={g.groupId} style={styles.groupBtn} onClick={() => onGroupPick(g.groupId)}>
                                {g.groupName}
                            </button>
                        ))}
                        {(!result.groupOptions || result.groupOptions.length === 0) && (
                            <div style={styles.resultSub}>Сегодня в этом помещении нет занятий по расписанию</div>
                        )}
                    </div>
                    <button style={styles.cancelBtn} onClick={() => setResult(null)}>Отмена</button>
                </div>
            )}

            {result && result.outcome !== 'no_active_session' && (
                <div style={{ ...styles.resultCard, background: style?.bg || '#334155' }}>
                    <div style={styles.resultTitle}>{style?.title || result.outcome}</div>
                    {result.studentName && <div style={styles.resultSub}>{result.studentName}</div>}
                    {result.amountCharged != null && (
                        <div style={styles.resultSub}>{result.amountCharged} ₽</div>
                    )}
                    {result.outcome === 'pending_confirmation' && (
                        <div style={styles.resultSub}>Сотрудник, отсканируйте свой QR для подтверждения</div>
                    )}
                    {result.requireWristband && result.outcome.startsWith('checked_in') && (
                        <div style={styles.resultSub}>
                            {result.wristbandPrintMode === 'name_date'
                                ? 'Печатаю браслет с именем…'
                                : 'Заберите браслет'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh', background: '#0f172a', color: '#fff',
        display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 24px', fontSize: 18, fontWeight: 600, borderBottom: '1px solid #1e293b',
    },
    logoutBtn: {
        background: 'transparent', border: '1px solid #475569', color: '#94a3b8',
        borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13,
    },
    scanArea: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 16, padding: 24,
    },
    qrRegion: { width: 320, maxWidth: '90vw' },
    cameraError: { color: '#fbbf24', maxWidth: 400, textAlign: 'center' },
    hint: { fontSize: 18, color: '#94a3b8' },
    hiddenInput: { position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' },
    manualForm: { display: 'flex', gap: 8, marginTop: 24 },
    manualInput: { padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', minWidth: 260 },
    manualBtn: {
        padding: '10px 18px', borderRadius: 8, border: 'none',
        background: 'var(--sp-primary, #2563eb)', color: '#fff', cursor: 'pointer',
    },
    resultCard: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 12, padding: 24,
    },
    resultTitle: { fontSize: 36, fontWeight: 800 },
    resultSub: { fontSize: 22 },
    groupList: { display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', maxWidth: 600 },
    groupBtn: {
        padding: '14px 22px', fontSize: 18, borderRadius: 10, border: 'none',
        background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer',
    },
    cancelBtn: {
        marginTop: 16, padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)',
        background: 'transparent', color: '#fff', cursor: 'pointer',
    },
};
